import {Canvas, FabricObject, util} from 'fabric';
import {FloorPlanItem} from '../types';
import {
    createShapeElement,
    createTableGroup,
    createTextElement,
    createWallElement,
} from './fabric-factory';
import {buildNormalizedItemFromObject} from './resize-utils';
import {syncFloorPlanItemTransform} from './snap-geometry';

export interface SnapshotItem {
    floorPlanItem: FloorPlanItem;
    left: number;
    top: number;
    angle: number;
    scaleX: number;
    scaleY: number;
}

export type Snapshot = SnapshotItem[];

export function captureCanvas(canvas: Canvas): Snapshot {
    return canvas
        .getObjects()
        .filter((obj): obj is FabricObject & {
            floorPlanItem: FloorPlanItem
        } => !!obj.floorPlanItem)
        .map(obj => {
            const center = obj.getCenterPoint();
            const decomposition = util.qrDecompose(obj.calcTransformMatrix());
            const left = center.x;
            const top = center.y;
            const angle = decomposition.angle ?? obj.angle ?? 0;
            const normalizedItem = buildNormalizedItemFromObject(obj) ?? obj.floorPlanItem!;

            return {
                floorPlanItem: structuredClone(syncFloorPlanItemTransform(normalizedItem, left, top, angle)),
                left,
                top,
                angle,
                scaleX: 1,
                scaleY: 1,
            };
        });
}

export function restoreCanvas(
    canvas: Canvas,
    snapshot: Snapshot,
    showSeatsAroundTable: boolean,
) {
    canvas.discardActiveObject();
    canvas.getObjects().forEach(obj => canvas.remove(obj));

    for (const item of snapshot) {
        let element: FabricObject | null = null;
        const fp = item.floorPlanItem;

        if (fp.table) {
            element = createTableGroup(fp, showSeatsAroundTable);
        } else if (fp.element) {
            if (fp.element.kind === 'text') {
                element = createTextElement(fp);
            } else if (fp.element.kind === 'shape' && fp.element.shape) {
                element = createShapeElement(fp);
            } else if (fp.element.kind === 'wall') {
                element = createWallElement(fp);
            }
        }

        if (element) {
            element.set({
                left: item.left,
                top: item.top,
                angle: item.angle,
                scaleX: item.scaleX,
                scaleY: item.scaleY,
            });
            element.setCoords();
            canvas.add(element);
        }
    }

    canvas.renderAll();
}
